import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { SYSTEM_SCHEDULE, type LibrarySystem } from '../utils/librarySystem'

interface ScheduleFutureModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (scheduledTime: string) => void
  date: string
  museumId: string
  passName?: string
  isLoading?: boolean
  system?: LibrarySystem
}

export function ScheduleFutureModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  date, 
  museumId, 
  passName, 
  isLoading,
  system = 'kcls'
}: ScheduleFutureModalProps) {
  const [customTime, setCustomTime] = useState('')
  const [useCustomTime, setUseCustomTime] = useState(false)

  const schedule = SYSTEM_SCHEDULE[system]

  const getPacificUtcOffsetHours = (date: Date) => {
    const tzLabel = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Los_Angeles',
      timeZoneName: 'short'
    }).formatToParts(date).find((part) => part.type === 'timeZoneName')?.value ?? ''

    return tzLabel.includes('PDT') ? 7 : 8
  }

  const toDateTimeLocalValue = (date: Date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    return `${year}-${month}-${day}T${hours}:${minutes}`
  }

  // Calculate the scheduled time using per-system config
  const calculateScheduledTime = (): Date | null => {
    if (!date || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return null

    const [year, month, day] = date.split('-').map(Number)
    
    // Calculate the date N days before (per system config)
    const targetDate = new Date(year, month - 1, day)
    targetDate.setDate(targetDate.getDate() - schedule.advanceDays)
    
    const schedYear = targetDate.getFullYear()
    const schedMonth = targetDate.getMonth()
    const schedDay = targetDate.getDate()
    
    // Match the backend logic: choose the local Pacific opening time and
    // convert it to the correct UTC instant based on PST vs PDT.
    const refUtc = new Date(Date.UTC(schedYear, schedMonth, schedDay, 20, 0, 0, 0))
    const pacificUtcOffsetHours = getPacificUtcOffsetHours(refUtc)
    const openHourUTC = schedule.openHourPacific + pacificUtcOffsetHours
    
    return new Date(Date.UTC(schedYear, schedMonth, schedDay, openHourUTC, 0, 0, 0))
  }

  const scheduledTime = calculateScheduledTime()

  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    window.addEventListener('keydown', handleEscape)
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = 'unset'
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setUseCustomTime(false)
      setCustomTime('')
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && scheduledTime) {
      setCustomTime(toDateTimeLocalValue(scheduledTime))
    }
  }, [isOpen, scheduledTime])

  if (!isOpen || !scheduledTime) return null

  const formattedDate = new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })

  const formattedScheduledTime = scheduledTime.toLocaleString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'America/Los_Angeles', // Force PST/PDT timezone
    timeZoneName: 'short'
  })

  const handleConfirm = () => {
    if (useCustomTime && customTime) {
      // datetime-local is a wall-clock local time, so convert it to an
      // unambiguous instant before sending it to the server.
      onConfirm(new Date(customTime).toISOString())
    } else {
      // Use calculated time
      onConfirm(scheduledTime!.toISOString())
    }
  }

  const isValidCustomTime = useCustomTime ? customTime !== '' : true

  return createPortal(
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
        <div 
          className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full transform transition-all"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                Schedule Automatic Booking
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Book automatically when spots open
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isLoading}
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Museum Info */}
            {passName && (
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <svg className="w-6 h-6 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-700 mb-1">Museum</p>
                    <p className="text-lg font-semibold text-gray-900">{passName}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Target Date */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700 mb-1">Target Date</p>
                  <p className="text-lg font-semibold text-gray-900">{formattedDate}</p>
                </div>
              </div>
            </div>

            {/* Museum ID */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <svg className="w-6 h-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-700 mb-1">Pass ID</p>
                  <p className="text-sm font-mono text-gray-600 bg-white px-2 py-1 rounded">{museumId}</p>
                </div>
              </div>
            </div>

            {/* Scheduled Time Section */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-4 border border-orange-100">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <svg className="w-6 h-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-1">Scheduled Execution Time</p>
                    <p className="text-lg font-semibold text-gray-900">{formattedScheduledTime}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Runs {schedule.advanceDays} days before target date at {schedule.openTimeLabel}
                    </p>
                  </div>

                  {/* Debug: Custom Time Option */}
                  <div className="pt-3 border-t border-orange-200">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useCustomTime}
                        onChange={(e) => setUseCustomTime(e.target.checked)}
                        className="w-4 h-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Debug: Use custom time
                      </span>
                    </label>

                    {useCustomTime && (
                      <div className="mt-2">
                        <input
                          type="datetime-local"
                          value={customTime}
                          onChange={(e) => setCustomTime(e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                          placeholder="Select custom time"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          For debugging: Schedule booking at a specific time
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="flex-1 text-sm text-blue-800">
                  <p className="font-medium mb-1">What happens next?</p>
                  <ul className="list-disc list-inside space-y-1 text-blue-700">
                    <li>Booking will be scheduled on the server</li>
                    <li>Server will automatically execute at the scheduled time</li>
                    <li>You can view progress in the "Scheduled" page</li>
                    <li>No need to keep your browser open</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 p-6 bg-gray-50 rounded-b-2xl">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-3 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading || !isValidCustomTime}
              className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-gradient-to-r from-orange-600 to-amber-600 rounded-xl hover:from-orange-700 hover:to-amber-700 transition-all shadow-lg shadow-orange-500/30 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>Scheduling...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Schedule Booking</span>
                </>
              )}
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
